// Reemplazos de aprobadores de Muestras (Tecnoboga y Vicsa): para cubrir a alguien por un tiempo (vacaciones, licencia) se indica a quién
// reemplaza esta persona. Tecnoboga asigna aprobadores por solicitud: verá y podrá decidir lo que le llegaría a esa persona, en cualquier
// paso. Vicsa decide por canal y Product Manager: heredará los canales y el PM de esa persona (los pasos que pueda decidir siguen siendo
// los de su propio perfil). La persona reemplazada conserva su acceso. El cambio es inmediato; sin fecha de término no vence.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Save, Trash2, Users } from 'lucide-react';
import { usuariosAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';

const hoyISO = () => new Date().toLocaleDateString('en-CA'); // AAAA-MM-DD en la hora local
const claseCampo = 'rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50';
const aEditable = (r) => ({ email: r.email, nombre: r.nombre, desde: r.desde || '', hasta: r.hasta || '' });
const aEnvio = (lista) => lista.map((r) => ({ email: r.email, desde: r.desde || null, hasta: r.hasta || null }));

export default function ReemplazosApp({ userId, appCodigo, onCargado }) {
  const { showToast } = useToast();
  const [datos, setDatos] = useState(null);
  const [lista, setLista] = useState([]);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [elegido, setElegido] = useState('');

  // El padre usa a quiénes cubre hoy (con lo que heredaría) para explicar qué verá y podrá aprobar
  const alCargar = useRef(onCargado);
  alCargar.current = onCargado;
  const aplicar = useCallback((d) => {
    setDatos(d);
    setLista((d.reemplaza || []).map(aEditable));
    alCargar.current?.((d.reemplaza || []).filter((r) => r.vigente));
  }, []);

  useEffect(() => {
    let vivo = true;
    setDatos(null);
    setError('');
    usuariosAPI
      .getReemplazosApp(userId, appCodigo)
      .then((r) => vivo && aplicar(r.data))
      .catch((err) => vivo && setError(err.response?.data?.message || 'No se pudieron cargar los reemplazos'));
    return () => {
      vivo = false;
    };
  }, [userId, appCodigo, aplicar]);

  const sucio = useMemo(() => datos && JSON.stringify(aEnvio(lista)) !== JSON.stringify(aEnvio((datos.reemplaza || []).map(aEditable))), [datos, lista]);
  const opciones = useMemo(() => (datos?.disponibles || []).filter((p) => !lista.some((r) => r.email === p.email)), [datos, lista]);
  const hoy = hoyISO();
  const invalida = lista.some((r) => r.desde && r.hasta && r.hasta < r.desde);

  const agregar = () => {
    const p = opciones.find((o) => o.email === elegido);
    if (!p) return;
    setLista((prev) => [...prev, { email: p.email, nombre: p.nombre, desde: hoy, hasta: '' }]);
    setElegido('');
  };
  const cambiar = (email, campo, valor) => setLista((prev) => prev.map((r) => (r.email === email ? { ...r, [campo]: valor } : r)));
  const quitar = (email) => setLista((prev) => prev.filter((r) => r.email !== email));

  const guardar = async () => {
    setGuardando(true);
    try {
      const r = await usuariosAPI.setReemplazosApp(userId, appCodigo, { reemplazos: aEnvio(lista) });
      aplicar(r.data);
      showToast('Listo', 'Reemplazos actualizados', 'success');
    } catch (err) {
      showToast('Error', err.response?.data?.message || 'No se pudieron guardar los reemplazos', 'error');
    } finally {
      setGuardando(false);
    }
  };

  if (error) return <p className="text-xs text-amber-700 dark:text-amber-400">{error}</p>;
  if (!datos) return <p className="text-xs text-gray-500">Cargando reemplazos…</p>;

  const cubiertoPor = (datos.reemplazadoPor || []).filter((r) => r.vigente);

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-4 py-3 space-y-3">
      <div>
        <h5 className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300">
          <Users className="w-3.5 h-3.5" /> Reemplaza a
        </h5>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          {appCodigo === 'muestras-vicsa'
            ? 'Heredará los canales y el Product Manager de estas personas: verá y podrá aprobar lo que les llegaría a ellas, en los pasos que su propio perfil puede decidir. Ellas conservan su acceso; quien decida primero cierra el paso.'
            : 'Verá y podrá aprobar lo que le llegaría a estas personas, en el paso que sea. Ellas conservan su acceso; quien decida primero cierra el paso.'}
        </p>
      </div>

      {lista.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No reemplaza a nadie.</p>
      ) : (
        <ul className="space-y-2">
          {lista.map((r) => (
            <li key={r.email} className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-3 py-2">
              <span className="flex-1 min-w-0 text-sm font-medium text-gray-900 dark:text-white truncate" title={r.email}>{r.nombre}</span>
              <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                Desde
                <input type="date" value={r.desde} onChange={(e) => cambiar(r.email, 'desde', e.target.value)} className={claseCampo} />
              </label>
              <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                Hasta
                <input type="date" value={r.hasta} min={r.desde || undefined} onChange={(e) => cambiar(r.email, 'hasta', e.target.value)} className={claseCampo} title="Sin fecha: no vence hasta que lo quites" />
              </label>
              <button type="button" onClick={() => quitar(r.email)} aria-label={`Quitar el reemplazo de ${r.nombre}`} className="p-1.5 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30">
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <select value={elegido} onChange={(e) => setElegido(e.target.value)} className={`${claseCampo} flex-1 min-w-0`} aria-label="Persona a la que reemplaza">
          <option value="">Elegir a quién reemplaza…</option>
          {opciones.map((p) => (
            <option key={p.email} value={p.email}>{p.nombre}{p.cargo ? ` · ${p.cargo}` : ''}</option>
          ))}
        </select>
        <button type="button" disabled={!elegido} onClick={agregar} className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50">
          <Plus className="w-3.5 h-3.5" /> Agregar
        </button>
      </div>

      {invalida && <p className="text-xs text-red-600">La fecha de término no puede ser anterior al inicio.</p>}
      {sucio && (
        <div className="flex justify-end">
          <button type="button" disabled={guardando || invalida} onClick={guardar} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
            <Save className="w-3.5 h-3.5" /> {guardando ? 'Guardando…' : 'Guardar reemplazos'}
          </button>
        </div>
      )}

      {cubiertoPor.length > 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400">Hoy lo reemplaza: {cubiertoPor.map((r) => r.nombre).join(', ')}.</p>
      )}
    </div>
  );
}
